import * as React from 'react';
import { DataTable, DataRow, DataCell } from 'components/basic/DataTable';
import { ConversionReportArchiveSummaryModel } from 'client/ApiClient';

export interface ArchivedConversionsColumnFilters {
    conversionName: string;
    conversionType: string;
    clientName: string;
    runType: string;
    createdByName: string;
    goLiveDate: string;
}

interface ArchivedConversionsGridProps {
    records: ConversionReportArchiveSummaryModel[];
    searchText: string;
    showFilters: boolean;
    columnFilters: ArchivedConversionsColumnFilters;
    onSearchChange: (value: string) => void;
    onToggleFilters: () => void;
    onColumnFilterChange: (column: keyof ArchivedConversionsColumnFilters, value: string) => void;
    onClearFilters: () => void;
    onRowClick: (id: string) => void;
}

interface ArchivedConversionsGridState {
    openFilterColumn: keyof ArchivedConversionsColumnFilters | null;
    sortColumn: keyof ArchivedConversionsColumnFilters | null;
    sortDescending: boolean;
}

interface ColumnConfig {
    key: keyof ArchivedConversionsColumnFilters;
    label: string;
    width: string;
}

const COLUMNS: ColumnConfig[] = [
    { key: 'conversionName', label: 'Conversion Name', width: '20%' },
    { key: 'conversionType', label: 'Conversion Type', width: '15%' },
    { key: 'clientName', label: 'Client Name', width: '15%' },
    { key: 'runType', label: 'Run Type', width: '10%' },
    { key: 'createdByName', label: 'Created By', width: '15%' },
    { key: 'goLiveDate', label: 'Go Live Date', width: '12%' },
];

export class ArchivedConversionsGrid extends React.Component<ArchivedConversionsGridProps, ArchivedConversionsGridState> {
    private containerRef = React.createRef<HTMLTableSectionElement>();

    constructor(props: ArchivedConversionsGridProps) {
        super(props);
        this.state = { openFilterColumn: null, sortColumn: null, sortDescending: false };
    }

    componentDidMount() {
        document.addEventListener('mousedown', this.handleDocumentMouseDown);
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleDocumentMouseDown);
    }

    private handleDocumentMouseDown = (event: MouseEvent) => {
        if (this.state.openFilterColumn && this.containerRef.current && !this.containerRef.current.contains(event.target as Node)) {
            this.setState({ openFilterColumn: null });
        }
    };

    private toggleFilterColumn = (column: keyof ArchivedConversionsColumnFilters) => {
        this.setState(prev => ({ openFilterColumn: prev.openFilterColumn === column ? null : column }));
    };

    private handleSelectFilterValue = (column: keyof ArchivedConversionsColumnFilters, value: string) => {
        this.props.onColumnFilterChange(column, value);
        this.setState({ openFilterColumn: null });
    };

    private handleSort = (column: keyof ArchivedConversionsColumnFilters) => {
        this.setState(prev => ({
            sortColumn: column,
            sortDescending: prev.sortColumn === column ? !prev.sortDescending : false,
        }));
    };

    // Go Live Date falls back to Completion Date, and then Created Date, when not set -
    // this mirrors the ORDER BY COALESCE(...) logic used by the underlying stored procedure.
    private getEffectiveGoLiveDate(record: ConversionReportArchiveSummaryModel): Date | undefined {
        return record.goLiveDate ?? record.completionDate ?? record.createdDate;
    }

    private getColumnDisplayValue(record: ConversionReportArchiveSummaryModel, column: keyof ArchivedConversionsColumnFilters): string {
        if (column === 'goLiveDate') {
            return this.formatDate(this.getEffectiveGoLiveDate(record));
        }
        return record[column] ?? '';
    }

    private getColumnSortValue(record: ConversionReportArchiveSummaryModel, column: keyof ArchivedConversionsColumnFilters): string | number {
        if (column === 'goLiveDate') {
            const date = this.getEffectiveGoLiveDate(record);
            return date ? new Date(date).getTime() : 0;
        }
        return (record[column] ?? '').toLowerCase();
    }

    private getDistinctColumnValues(column: keyof ArchivedConversionsColumnFilters): string[] {
        const { records } = this.props;
        const values = new Set<string>();
        records.forEach(record => {
            const value = this.getColumnDisplayValue(record, column);
            if (value) {
                values.add(value);
            }
        });
        return Array.from(values).sort((a, b) => a.localeCompare(b));
    }

    private getFilteredRecords(): ConversionReportArchiveSummaryModel[] {
        const { records, searchText, columnFilters } = this.props;
        const normalizedSearch = searchText.trim().toLowerCase();

        return records.filter(record => {
            const searchMatches = !normalizedSearch || [
                record.conversionName,
                record.conversionType,
                record.clientName,
                record.runType,
                record.createdByName,
            ].some(value => value?.toLowerCase().includes(normalizedSearch));

            if (!searchMatches) {
                return false;
            }

            return this.recordMatchesColumnFilters(record, columnFilters);
        });
    }

    private recordMatchesColumnFilters(
        record: ConversionReportArchiveSummaryModel,
        columnFilters: ArchivedConversionsColumnFilters): boolean {
        return COLUMNS.every(column => {
            const filterValue = columnFilters[column.key];
            if (!filterValue) {
                return true;
            }
            return this.getColumnDisplayValue(record, column.key) === filterValue;
        });
    }

    private getSortedRecords(records: ConversionReportArchiveSummaryModel[]): ConversionReportArchiveSummaryModel[] {
        const { sortColumn, sortDescending } = this.state;

        if (!sortColumn) {
            return records;
        }

        return [...records].sort((a, b) => {
            const aVal = this.getColumnSortValue(a, sortColumn);
            const bVal = this.getColumnSortValue(b, sortColumn);

            if (aVal < bVal) return sortDescending ? 1 : -1;
            if (aVal > bVal) return sortDescending ? -1 : 1;
            return 0;
        });
    }

    private formatDate(date: Date | null | undefined): string {
        return date ? new Date(date).toLocaleDateString() : '—';
    }

    public render() {
        const {
            onRowClick,
            searchText,
            onSearchChange,
            showFilters,
            onToggleFilters,
            columnFilters,
            onClearFilters,
        } = this.props;
        const { openFilterColumn } = this.state;
        const filteredRecords = this.getSortedRecords(this.getFilteredRecords());

        return (
            <DataTable>
                <thead ref={this.containerRef}>
                    <tr className='nonFullWidthRow' style={{ minWidth: 'unset' }}>
                        <th colSpan={7} style={{ padding: '6px 8px', borderBottom: '1px solid #dee2e6' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '12px', width: '100%' }}>
                                <span style={{ fontWeight: 'bold', fontSize: '21px', whiteSpace: 'nowrap', flexShrink: 0 }}>Archived Conversion Projects</span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
                                    <a
                                        role="button"
                                        tabIndex={0}
                                        style={{ color: '#007bff', cursor: 'pointer', whiteSpace: 'nowrap', textDecoration: 'none' }}
                                        onClick={onToggleFilters}
                                    >
                                        {showFilters ? 'Hide Filters' : 'Show Filters'}
                                    </a>
                                    <input
                                        type="text"
                                        className="form-control"
                                        style={{ maxWidth: '360px', flex: '1 1 360px' }}
                                        placeholder="Search"
                                        value={searchText}
                                        onChange={e => onSearchChange(e.target.value)}
                                    />
                                    <button type="button" className="btn btn-secondary material-icon" aria-label="Clear search" onClick={onClearFilters}>
                                        close
                                    </button>
                                </div>
                            </div>
                        </th>
                    </tr>
                    <tr className='nonFullWidthRow' style={{ minWidth: 'unset' }}>
                        <th style={{ width: '3%', textAlign: 'center' }}>#</th>
                        {COLUMNS.map(column => (
                            <th key={column.key} style={{ width: column.width, textAlign: 'center', position: 'relative', overflow: 'visible' }}>
                                {column.label}
                                <i
                                    className="fa fa-sort"
                                    role="button"
                                    aria-label={`Sort by ${column.label}`}
                                    style={{ marginLeft: '6px', cursor: 'pointer' }}
                                    onClick={() => this.handleSort(column.key)}
                                ></i>
                                {showFilters && (
                                    <i
                                        className="fa fa-caret-down"
                                        role="button"
                                        aria-label={`Filter ${column.label}`}
                                        style={{ marginLeft: '4px', cursor: 'pointer', color: columnFilters[column.key] ? '#007bff' : undefined }}
                                        onClick={() => this.toggleFilterColumn(column.key)}
                                    ></i>
                                )}
                                {openFilterColumn === column.key && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            top: '100%',
                                            left: 0,
                                            zIndex: 1000,
                                            background: '#fff',
                                            border: '1px solid #ccc',
                                            boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                                            padding: '4px 0',
                                            minWidth: '160px',
                                            maxHeight: '220px',
                                            overflowY: 'auto',
                                            fontWeight: 'normal',
                                            textAlign: 'left',
                                        }}
                                        onClick={e => e.stopPropagation()}
                                    >
                                        <div
                                            style={{ padding: '4px 12px', cursor: 'pointer', fontStyle: 'italic' }}
                                            onClick={() => this.handleSelectFilterValue(column.key, '')}
                                        >
                                            (All)
                                        </div>
                                        {this.getDistinctColumnValues(column.key).map(value => (
                                            <div
                                                key={value}
                                                style={{
                                                    padding: '4px 12px',
                                                    cursor: 'pointer',
                                                    backgroundColor: columnFilters[column.key] === value ? '#e9ecef' : undefined,
                                                }}
                                                onClick={() => this.handleSelectFilterValue(column.key, value)}
                                            >
                                                {value}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className="nonEntity">
                    {filteredRecords.map((record, index) => (
                        <DataRow key={record.id} id={record.id}
                            onClick={() => onRowClick(record.id)}
                            style={{ cursor: 'pointer' }}>
                            <DataCell style={{ width: '3%' }}>{index + 1}</DataCell>
                            <DataCell style={{ width: '20%' }} title={record.conversionName}>{record.conversionName}</DataCell>
                            <DataCell style={{ width: '15%' }} title={record.conversionType}>{record.conversionType}</DataCell>
                            <DataCell style={{ width: '15%' }} title={record.clientName}>{record.clientName}</DataCell>
                            <DataCell style={{ width: '10%' }} title={record.runType}>{record.runType}</DataCell>
                            <DataCell style={{ width: '15%' }} title={record.createdByName}>{record.createdByName}</DataCell>
                            <DataCell style={{ width: '12%' }}>{this.formatDate(this.getEffectiveGoLiveDate(record))}</DataCell>
                        </DataRow>
                    ))}
                    {filteredRecords.length === 0 && (
                        <tr>
                            <td colSpan={7} className="text-center">No archived conversion reports found.</td>
                        </tr>
                    )}
                </tbody>
            </DataTable>
        );
    }
}
